import { useCallback, useEffect, useMemo, useState, } from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useManualCheckMutation, usePostNumberPaymentMutation, usePostPayMutation } from "@/pages/main/components/report/report-api";
import { useDispatch, useSelector } from "react-redux";
import { clearManualPaymentStep2Params, selectManualPaymentStep2Params, selectShowManualPaymentStep2 } from "./modalsSlice";
import { extractManualDebtAmount, normalizeManualPlateNumber } from "@/utils/manual-payment";

const ManualPaymentModalStep2 = () => {
  const show = useSelector(selectShowManualPaymentStep2);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOFD, setIsOFD] = useState(false);
  const [localServiceResponseData, setLocalServiceResponseData] = useState(null);
  //modalParams?.plateNumberPay, // TODO хуй знает зачем это значение, вникнуть потом
  const modalParams = useSelector(selectManualPaymentStep2Params);
  
  const [manualCheck, { 
    data: HTML, 
    isSuccess: HTMLSuccess,
    reset: resetManualCheck,
  }] = useManualCheckMutation();

  const [paymentPay, {
    isSuccess: paymentSuccess,
    isError: paymentError,
    error: paymentErrorData,
    isLoading: paymentPayLoading,
    reset: resetPaymentPay,
  }] = usePostNumberPaymentMutation();
  
  const [paymentCheck, {
    data: paymentCheckData,
    isLoading: paymentCheckLoading,
    isSuccess: paymentCheckSuccess,
    isError: paymentCheckError,
    error: paymentCheckErrorData,
    reset: resetPaymentCheck,
  }] = usePostPayMutation();

  const normalizedPlateNumber = useMemo(() => (normalizeManualPlateNumber(
      modalParams?.resolvedPaymentPlate || modalParams?.plateNumberPay
  )), [modalParams])

  const handleLocalService = useCallback(async () => {
    try {
      // Локальный ОФД-агент кассы на 127.0.0.1. TLS у агента нет, запрос не покидает хост.
      // nosemgrep
      const response = await fetch('http://127.0.0.1:8080/TradeOperation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentCheckData?.OFD)
      })
      if (!response.ok) {
        throw new Error(`TradeOperation failed: ${response.status}`)
      }
      const data = await response.json()
      setLocalServiceResponseData(data)
    } catch (err) {
      setLocalServiceResponseData(null)
      console.error(err);
    }
  }, [paymentCheckData?.OFD]);

  const statusAlert = useMemo(() => (
    !normalizedPlateNumber ? { 
      severity:'error', 
      message: t('car_number_is_required') 
    } : paymentSuccess ? {
      severity: 'info',
      message: t('payment_successful')
    } : paymentError ? { 
      severity: 'error',
      message: paymentErrorData?.data?.message 
        || paymentErrorData?.message
        || t('pay_error') 
    } : paymentCheckError ? {
      severity:'error',
      message: paymentCheckErrorData?.data?.message 
        || paymentCheckErrorData?.message 
        || t('tariff_not_found') 
    } : undefined
  ), [
    normalizedPlateNumber, 
    paymentSuccess, 
    paymentError, 
    paymentErrorData, 
    paymentCheckError, 
    paymentCheckErrorData, 
    t
  ]); 

  const handleSubmit = async () => {
    if (!normalizedPlateNumber) return
    const paymentAmount = extractManualDebtAmount({
      amount: modalParams?.resolvedPaymentAmount
    })
    try {
      await paymentCheck({
        body: {
          plateNumber: normalizedPlateNumber,
          buildingId: modalParams?.buildingId,
          amount: paymentAmount
        },
        ofd: isOFD
      }).unwrap()
    } catch (err) {
      console.log(err)
    }
  };

  const manualPay = async () => {
    //  Использование отредактированной суммы
    // const finalAmount = manualAmount !== null ? manualAmount : manualCheckData?.amount
    if (!normalizedPlateNumber) return
    
    const paymentAmount = extractManualDebtAmount({
      amount: modalParams?.resolvedPaymentAmount
    })
    
    try {
      await paymentPay({
        plateNumber: normalizedPlateNumber,
        buildingId: modalParams?.buildingId,
        amount: paymentAmount
      }).unwrap()
      // dispatch(clearManualPaymentStep2Params())
      // Сброс состояния
      // setManualAmount(null)
      // setAmountEdited(false)
      
    } catch (err) {
      console.log(err);
      
    }
  }

  const handleManualCheck = useCallback(async (body, id) => {
    try {
      const payload = { ofd: body }
      await manualCheck({ body: payload, id }).unwrap()
    } catch (err) {
      console.log(err)
    }
  }, [manualCheck]);

  const handleSubmitClick = async () => {
    if (isOFD) {
      await handleSubmit()
    } else {
      await manualPay()
    }
  }

  const handleClose = () => {
    dispatch(clearManualPaymentStep2Params());
    resetManualCheck()
    resetPaymentPay()
    resetPaymentCheck()
    setLocalServiceResponseData(null);
  }

  useEffect(() => {
    if (paymentCheckSuccess) {
      handleLocalService()
    }
  }, [handleLocalService, paymentCheckSuccess]);

  useEffect(() => {
    if (HTMLSuccess && localServiceResponseData) {
      const newWindow = window.open('', '_blank')
      if (!newWindow) {
        console.error('Unable to open payment receipt window')
        return
      }
      newWindow.document.write(HTML)
      newWindow.document.close()
      newWindow.focus()
      newWindow.print()
    }
  }, [HTML, HTMLSuccess, localServiceResponseData]);
  
  useEffect(() => {
    if (paymentCheckSuccess && localServiceResponseData)
      handleManualCheck(localServiceResponseData, paymentCheckData?.txnId)
  }, [
    handleManualCheck,
    paymentCheckData?.txnId,
    paymentCheckSuccess,
    localServiceResponseData
  ])
  
  return (
    <Dialog 
      open={show} 
      onClose={handleClose}
    >
      <DialogTitle>{`${t('payment')} 2/2`}</DialogTitle>
      <DialogContent>
        {
          statusAlert &&
          <Alert 
            sx={{mb: 2}}
            severity={ statusAlert.severity }
          >
            { statusAlert.message }
          </Alert>
        }
        <DialogContentText sx={{ mb: 2 }}>
          {t('license_plate')}: {modalParams?.resolvedPaymentPlate || '-'}
        </DialogContentText>
        <DialogContentText>
          {t('debt_amount')}, {t('tenge')}: {modalParams?.resolvedPaymentAmount || 0}
        </DialogContentText>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={isOFD}
                disabled={paymentPayLoading || paymentCheckLoading}
                onChange={() => setIsOFD(!isOFD)} 
              />
            }
            label={t('check')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          color="error" 
          onClick={handleClose}
        >
          {t('close')}
        </Button>
        {
          !paymentSuccess &&
          <Button
            onClick={handleSubmitClick}
            loading={paymentPayLoading || paymentCheckLoading}
            disabled={
              paymentPayLoading ||
              paymentCheckLoading ||
              Number(modalParams?.resolvedPaymentAmount || 0) <= 0
            }
          >
            {t('pay')}
          </Button>
        }
      </DialogActions>
    </Dialog>
  )
}

export default ManualPaymentModalStep2;